/* Manihar Gems — theme.js
   Vanilla JS behavior layer replacing the original React contexts/components.
   Depends on: Shopify AJAX Cart API (/cart/add.js, /cart/change.js, /cart.js),
   window.Shopify.money_format (set in layout/theme.liquid).
   Data-attribute contract used by the Liquid sections/snippets (see comments
   above each module) — keep these in sync when writing markup.
*/
(function () {
  "use strict";

  /* ----------------------------- utilities ----------------------------- */
  const qs = (sel, ctx) => (ctx || document).querySelector(sel);
  const qsa = (sel, ctx) => Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  const on = (el, evt, sel, handler) => {
    if (!el) return;
    el.addEventListener(evt, function (e) {
      const target = sel ? e.target.closest(sel) : el;
      if (target && el.contains(target)) handler(e, target);
    });
  };
  const debounce = (fn, wait) => {
    let t;
    return function () {
      clearTimeout(t);
      const args = arguments;
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  // Classic Shopify money formatter (reads window.Shopify.money_format).
  function formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    const fmt = format || (window.Shopify && window.Shopify.money_format) || "₹{{amount}}";
    function pad(n, width, z) {
      z = z || "0";
      n = n + "";
      return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? "," : thousands;
      decimal = decimal == null ? "." : decimal;
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split(".");
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      const cents2 = parts[1] ? decimal + parts[1] : "";
      return dollars + cents2;
    }
    let value = "";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholderRegex);
    if (!match) return fmt;
    switch (match[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }
  window.mgFormatMoney = formatMoney;

  function toast(message, tone) {
    let root = qs("[data-toast-root]");
    if (!root) {
      root = document.createElement("div");
      root.setAttribute("data-toast-root", "");
      root.className = "toast-container";
      document.body.appendChild(root);
    }
    const el = document.createElement("div");
    el.className = "toast" + (tone === "error" ? " toast--error" : "");
    el.textContent = message;
    root.appendChild(el);
    requestAnimationFrame(() => el.classList.add("is-visible"));
    setTimeout(() => {
      el.classList.remove("is-visible");
      setTimeout(() => el.remove(), 300);
    }, 3200);
  }
  window.mgToast = toast;

  /* ------------------------------ header -------------------------------
     [data-site-header]
     [data-nav-trigger][data-nav-key="women|men"]  -> hover opens mega menu
     [data-mega-menu][data-mega-key="women|men"]
     [data-mobile-menu-btn] / [data-mobile-drawer] / [data-mobile-close-btn]
     [data-search-btn] / [data-search-overlay] / [data-search-close] / [data-search-form] / [data-search-input]
  ------------------------------------------------------------------------ */
  function initHeader() {
    const header = qs("[data-site-header]");
    if (!header) return;

    const setScrolled = () => {
      const scrolled = window.scrollY > 40;
      header.classList.toggle("is-scrolled", scrolled);
    };
    setScrolled();
    window.addEventListener("scroll", debounce(setScrolled, 10));

    let hoverKey = null;
    const clearHover = () => {
      hoverKey = null;
      header.classList.remove("has-hover");
      qsa("[data-mega-menu]", header).forEach((m) => m.classList.remove("is-open"));
      qsa("[data-nav-trigger]", header).forEach((t) => t.classList.remove("is-active"));
    };
    header.addEventListener("mouseleave", clearHover);

    qsa("[data-nav-trigger]", header).forEach((trigger) => {
      trigger.addEventListener("mouseenter", () => {
        const key = trigger.getAttribute("data-nav-key");
        hoverKey = key;
        header.classList.add("has-hover");
        qsa("[data-mega-menu]", header).forEach((m) => {
          m.classList.toggle("is-open", m.getAttribute("data-mega-key") === key);
        });
        qsa("[data-nav-trigger]", header).forEach((t) => t.classList.toggle("is-active", t === trigger));
      });
    });
    qsa("[data-nav-plain]", header).forEach((el) => {
      el.addEventListener("mouseenter", clearHover);
    });

    // mobile drawer
    const mobileBtn = qs("[data-mobile-menu-btn]", header);
    const mobileDrawer = qs("[data-mobile-drawer]", header);
    const mobileClose = qs("[data-mobile-close-btn]", header);
    if (mobileBtn && mobileDrawer) {
      mobileBtn.addEventListener("click", () => {
        mobileDrawer.classList.add("is-open");
        document.documentElement.classList.add("no-scroll");
      });
    }
    if (mobileClose && mobileDrawer) {
      mobileClose.addEventListener("click", () => {
        mobileDrawer.classList.remove("is-open");
        document.documentElement.classList.remove("no-scroll");
      });
    }
    qsa("[data-mobile-drawer-close-link]", header).forEach((l) =>
      l.addEventListener("click", () => {
        mobileDrawer && mobileDrawer.classList.remove("is-open");
        document.documentElement.classList.remove("no-scroll");
      })
    );

    // search overlay
    const searchBtn = qs("[data-search-btn]", header);
    const searchOverlay = qs("[data-search-overlay]", header);
    const searchClose = qs("[data-search-close]", header);
    const searchInput = qs("[data-search-input]", header);
    if (searchBtn && searchOverlay) {
      searchBtn.addEventListener("click", () => {
        searchOverlay.classList.add("is-open");
        setTimeout(() => searchInput && searchInput.focus(), 50);
      });
    }
    if (searchClose && searchOverlay) {
      searchClose.addEventListener("click", () => searchOverlay.classList.remove("is-open"));
    }
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        searchOverlay && searchOverlay.classList.remove("is-open");
        mobileDrawer && mobileDrawer.classList.remove("is-open");
        clearHover();
      }
    });

    // native Shopify localization (country/currency) switcher
    qsa("[data-currency-switcher]", header).forEach((wrap) => {
      const toggle = qs("[data-currency-toggle]", wrap);
      if (!toggle) return;
      toggle.addEventListener("click", (e) => {
        e.stopPropagation();
        wrap.classList.toggle("is-open");
      });
      qsa("[data-currency-option]", wrap).forEach((opt) => {
        opt.addEventListener("click", (e) => {
          e.preventDefault();
          const form = wrap.closest("form");
          const input = form && qs('input[name="country_code"]', form);
          if (input) {
            input.value = opt.getAttribute("data-value");
            form.submit();
          }
        });
      });
    });
    document.addEventListener("click", () => {
      qsa("[data-currency-switcher].is-open").forEach((w) => w.classList.remove("is-open"));
    });
  }

  /* -------------------------------- cart --------------------------------
     Add-to-cart forms: <form data-add-to-cart-form action="/cart/add" method="post">
       standard Shopify field name="id" (variant id), name="quantity"
     Cart trigger buttons: [data-cart-open-btn]  [data-cart-count]
     Drawer: [data-cart-drawer] [data-cart-overlay] [data-cart-close-btn]
       [data-cart-items] + <template data-cart-line-template>
       inside a rendered line: [data-line-key] wrapper,
       [data-cart-qty-decrease] [data-cart-qty-increase] [data-qty-value]
       [data-cart-remove] [data-line-total]
       [data-cart-empty] shown when no items
       [data-cart-subtotal] [data-cart-checkout]
  ------------------------------------------------------------------------- */
  const Cart = (function () {
    let drawer, overlay, itemsRoot, lineTemplate, emptyState, subtotalEl, countEls;

    function collectEls() {
      drawer = qs("[data-cart-drawer]");
      overlay = qs("[data-cart-overlay]");
      itemsRoot = qs("[data-cart-items]");
      lineTemplate = qs("[data-cart-line-template]");
      emptyState = qs("[data-cart-empty]");
      subtotalEl = qs("[data-cart-subtotal]");
      countEls = qsa("[data-cart-count]");
    }

    function open() {
      if (!drawer) return;
      drawer.classList.add("is-open");
      overlay && overlay.classList.add("is-open");
      document.documentElement.classList.add("no-scroll");
    }
    function close() {
      if (!drawer) return;
      drawer.classList.remove("is-open");
      overlay && overlay.classList.remove("is-open");
      document.documentElement.classList.remove("no-scroll");
    }

    function renderLine(item) {
      if (!lineTemplate) return null;
      const node = lineTemplate.content.firstElementChild.cloneNode(true);
      node.setAttribute("data-line-key", item.key);
      const img = qs("[data-line-image]", node);
      if (img) {
        img.src = item.image || "";
        img.alt = item.product_title || item.title;
      }
      const title = qs("[data-line-title]", node);
      if (title) title.textContent = item.product_title || item.title;
      const variant = qs("[data-line-variant]", node);
      if (variant) {
        const parts = (item.options_with_values || [])
          .map((o) => o.value)
          .filter((v) => v && v.toLowerCase() !== "default title");
        variant.textContent = parts.join(" · ");
        variant.hidden = parts.length === 0;
      }
      const qtyVal = qs("[data-qty-value]", node);
      if (qtyVal) qtyVal.textContent = item.quantity;
      const lineTotal = qs("[data-line-total]", node);
      if (lineTotal) lineTotal.textContent = formatMoney(item.final_line_price);
      const dec = qs("[data-cart-qty-decrease]", node);
      const inc = qs("[data-cart-qty-increase]", node);
      const rem = qs("[data-cart-remove]", node);
      if (dec) dec.addEventListener("click", () => changeLine(item.key, item.quantity - 1));
      if (inc) inc.addEventListener("click", () => changeLine(item.key, item.quantity + 1));
      if (rem) rem.addEventListener("click", () => changeLine(item.key, 0));
      return node;
    }

    function render(cart) {
      if (!itemsRoot) return;
      itemsRoot.innerHTML = "";
      const hasItems = cart.item_count > 0;
      if (emptyState) emptyState.hidden = hasItems;
      itemsRoot.hidden = !hasItems;
      cart.items.forEach((item) => {
        const node = renderLine(item);
        if (node) itemsRoot.appendChild(node);
      });
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
      countEls.forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle("is-visible", cart.item_count > 0);
      });
      const footer = qs("[data-cart-footer]");
      if (footer) footer.hidden = !hasItems;
    }

    function refresh() {
      return fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then((r) => r.json())
        .then((cart) => {
          render(cart);
          return cart;
        })
        .catch(() => {});
    }

    function changeLine(key, quantity) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
      })
        .then((r) => r.json())
        .then((cart) => render(cart))
        .catch(() => toast("Could not update your bag.", "error"));
    }

    function add(formData, opts) {
      opts = opts || {};
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            toast((data && data.description) || "That item is unavailable.", "error");
            return null;
          }
          return refresh().then(() => {
            if (!opts.silent) open();
            return data;
          });
        })
        .catch(() => {
          toast("Could not add that to your bag.", "error");
          return null;
        });
    }

    // Decorative size swatches (see product.liquid) aren't real Shopify
    // variants for this catalog, so we validate them the same way the
    // original React PDP did: block add-to-cart until one is picked.
    function sizeSelected(form) {
      const sizeGroup = qs("[data-size-group]", form);
      if (!sizeGroup) return true;
      return !!qs("[data-size-swatch].is-active", sizeGroup);
    }

    function initForms() {
      qsa("[data-add-to-cart-form]").forEach((form) => {
        qsa("[data-size-swatch]", form).forEach((swatch) => {
          swatch.addEventListener("click", () => {
            qsa("[data-size-swatch]", form).forEach((s) => s.classList.remove("is-active"));
            swatch.classList.add("is-active");
            const label = qs("[data-size-active-label]", form);
            if (label) label.textContent = swatch.textContent.trim();
          });
        });
        form.addEventListener("submit", (e) => {
          e.preventDefault();
          if (!sizeSelected(form)) {
            toast("Please select a size.", "error");
            return;
          }
          const btn = qs("[data-add-to-cart-submit]", form) || qs('button[type="submit"]', form);
          if (btn) btn.classList.add("is-loading");
          add(new FormData(form)).finally(() => btn && btn.classList.remove("is-loading"));
        });
      });
    }

    function init() {
      collectEls();
      qsa("[data-cart-open-btn]").forEach((btn) => btn.addEventListener("click", open));
      qsa("[data-cart-close-btn]").forEach((btn) => btn.addEventListener("click", close));
      overlay && overlay.addEventListener("click", close);
      const checkoutBtn = qs("[data-cart-checkout]");
      if (checkoutBtn) checkoutBtn.addEventListener("click", () => (window.location.href = "/checkout"));
      initForms();
      refresh();
    }

    return { init, open, close, refresh, add, changeLine };
  })();
  window.MGCart = Cart;

  /* ------------------------------ wishlist -------------------------------
     Toggle buttons: [data-wishlist-toggle][data-handle][data-title][data-image][data-price][data-url]
     Header badge: [data-wishlist-count]
     Grid page: [data-wishlist-grid] + <template data-wishlist-item-template>
       [data-wishlist-empty]
  ------------------------------------------------------------------------- */
  const Wishlist = (function () {
    const KEY = "mg_wishlist";

    function getAll() {
      try {
        return JSON.parse(localStorage.getItem(KEY) || "[]");
      } catch (e) {
        return [];
      }
    }
    function save(list) {
      try {
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch (e) {}
    }
    function has(handle) {
      return getAll().some((p) => p.handle === handle);
    }
    function toggle(product) {
      let list = getAll();
      if (list.some((p) => p.handle === product.handle)) {
        list = list.filter((p) => p.handle !== product.handle);
      } else {
        list.push(product);
      }
      save(list);
      syncUI();
      return list;
    }

    function syncUI() {
      const list = getAll();
      qsa("[data-wishlist-count]").forEach((el) => {
        el.textContent = list.length;
        el.classList.toggle("is-visible", list.length > 0);
      });
      qsa("[data-wishlist-toggle]").forEach((btn) => {
        const handle = btn.getAttribute("data-handle");
        btn.classList.toggle("is-wished", list.some((p) => p.handle === handle));
      });
      renderGrid(list);
    }

    function renderGrid(list) {
      const grid = qs("[data-wishlist-grid]");
      if (!grid) return;
      const template = qs("[data-wishlist-item-template]");
      const empty = qs("[data-wishlist-empty]");
      grid.innerHTML = "";
      if (empty) empty.hidden = list.length > 0;
      grid.hidden = list.length === 0;
      if (!template) return;
      list.forEach((p) => {
        const node = template.content.firstElementChild.cloneNode(true);
        node.setAttribute("href", p.url);
        const img = qs("[data-item-image]", node);
        if (img) {
          img.src = p.image;
          img.alt = p.title;
        }
        const title = qs("[data-item-title]", node);
        if (title) title.textContent = p.title;
        const price = qs("[data-item-price]", node);
        if (price) price.textContent = formatMoney(p.price);
        const removeBtn = qs("[data-item-remove]", node);
        if (removeBtn) {
          removeBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle(p);
          });
        }
        grid.appendChild(node);
      });
    }

    function initButtons() {
      qsa("[data-wishlist-toggle]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle({
            handle: btn.getAttribute("data-handle"),
            title: btn.getAttribute("data-title"),
            image: btn.getAttribute("data-image"),
            price: parseInt(btn.getAttribute("data-price") || "0", 10),
            url: btn.getAttribute("data-url"),
          });
        });
      });
    }

    function init() {
      initButtons();
      syncUI();
    }

    return { init, has, toggle, getAll };
  })();
  window.MGWishlist = Wishlist;

  /* ----------------------------- newsletter -------------------------------
     Popup: [data-newsletter-slidein] [data-newsletter-dismiss]
     Forms (footer + popup) use Shopify's native `{% form 'customer' %}` tag,
     tagged contact[tags]=newsletter, and submit as a normal full page POST.
     On the page Shopify redirects back to, we check ?customer_posted=true
     to show a success toast and dismiss the popup.
  --------------------------------------------------------------------------- */
  function initNewsletter() {
    const DISMISS_KEY = "mg_news_dismissed";
    const slidein = qs("[data-newsletter-slidein]");
    if (slidein && !localStorage.getItem(DISMISS_KEY)) {
      setTimeout(() => slidein.classList.add("is-open"), 6500);
    }
    const dismiss = () => {
      try {
        localStorage.setItem(DISMISS_KEY, "1");
      } catch (e) {}
      slidein && slidein.classList.remove("is-open");
    };
    qsa("[data-newsletter-dismiss]").forEach((btn) => btn.addEventListener("click", dismiss));

    // Shopify's native customer form does a full-page submit; on the reload
    // it comes back to, look for its success/error query params.
    const params = new URLSearchParams(window.location.search);
    if (params.get("customer_posted") === "true") {
      toast("Welcome to Manihar Gems.");
      dismiss();
    }
  }

  /* -------------------------------- reveal --------------------------------
     [data-reveal] (optional data-reveal-delay="150") — fade-up on scroll.
     [data-reveal-mask-group] wrapping one or more .reveal-mask[data-mask-line]
     elements (each: <span class="reveal-mask" data-mask-line><span>Text</span></span>)
     — masked line-by-line reveal; lines are staggered via an inline
     transition-delay set here.
  --------------------------------------------------------------------------- */
  function initReveal() {
    qsa("[data-reveal-mask-group]").forEach((group) => {
      qsa("[data-mask-line]", group).forEach((line, i) => {
        line.style.transitionDelay = i * 120 + "ms";
      });
    });

    if (!("IntersectionObserver" in window)) {
      qsa("[data-reveal]").forEach((el) => el.classList.add("is-visible"));
      qsa("[data-reveal-mask-group]").forEach((group) => {
        qsa("[data-mask-line]", group).forEach((line) => line.classList.add("is-visible"));
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target.hasAttribute("data-reveal-mask-group")) {
              qsa("[data-mask-line]", entry.target).forEach((line) => line.classList.add("is-visible"));
            } else {
              entry.target.classList.add("is-visible");
            }
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "-10% 0px -10% 0px", threshold: 0.05 }
    );
    qsa("[data-reveal]").forEach((el) => {
      const delay = el.getAttribute("data-reveal-delay");
      if (delay) el.style.transitionDelay = delay + "ms";
      io.observe(el);
    });
    qsa("[data-reveal-mask-group]").forEach((group) => io.observe(group));
  }

  /* -------------------------- hero parallax + intro ------------------------
     [data-hero] wraps [data-hero-media] (parallax on scroll) and any
     [data-reveal-on-load] / [data-reveal-mask].reveal-on-load elements that
     should animate in immediately rather than waiting for IntersectionObserver.
  --------------------------------------------------------------------------- */
  function initHero() {
    const hero = qs("[data-hero]");
    if (!hero) return;
    const media = qs("[data-hero-media]", hero);
    if (media) {
      const onScroll = () => {
        const rect = hero.getBoundingClientRect();
        const total = rect.height || window.innerHeight;
        const progress = Math.min(Math.max(-rect.top / total, 0), 1);
        media.style.transform = "translateY(" + progress * 22 + "%) scale(" + (1 + progress * 0.15) + ")";
        const overlay = qs("[data-hero-overlay]", hero);
        if (overlay) overlay.style.opacity = 0.32 + progress * 0.28;
      };
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
    }
    qsa("[data-reveal-mask-group]", hero).forEach((group) => {
      qsa("[data-mask-line]", group).forEach((line, i) => {
        line.style.transitionDelay = i * 120 + "ms";
      });
    });
    requestAnimationFrame(() => {
      qsa("[data-reveal-on-load]", hero).forEach((el) => el.classList.add("is-visible"));
      qsa("[data-reveal-mask-group] [data-mask-line]", hero).forEach((line) => line.classList.add("is-visible"));
    });
  }

  /* ------------------------------ accordion -------------------------------
     [data-accordion-trigger] toggles an `is-open` class on its closest
     .pdp-accordion-item ancestor, which the CSS uses to show/hide the
     adjacent [data-accordion-panel] and rotate the chevron.
  --------------------------------------------------------------------------- */
  function initAccordion() {
    qsa("[data-accordion-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const item = trigger.closest(".pdp-accordion-item") || trigger.parentElement;
        item.classList.toggle("is-open");
      });
    });
  }

  /* -------------------------- item details toggle --------------------------
     [data-item-details] wraps a half-visible [data-item-details-body] and a
     [data-item-details-toggle] button containing a
     [data-item-details-toggle-label] span. Clicking the button toggles an
     `is-expanded` class on the wrapper (CSS lifts the max-height/fade mask)
     and swaps the label between "Read more" / "Read less".
  --------------------------------------------------------------------------- */
  function initItemDetailsToggle() {
    qsa("[data-item-details-toggle]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const wrap = btn.closest("[data-item-details]");
        if (!wrap) return;
        const expanded = wrap.classList.toggle("is-expanded");
        const label = qs("[data-item-details-toggle-label]", btn);
        if (label) label.textContent = expanded ? "Read less" : "Read more";
      });
    });
  }

  /* --------------------------- quantity steppers ---------------------------
     Standalone qty steppers (PDP): [data-qty-decrease] [data-qty-input] [data-qty-increase]
  --------------------------------------------------------------------------- */
  function initQtySteppers() {
    qsa("[data-qty-stepper]").forEach((wrap) => {
      const input = qs("[data-qty-input]", wrap);
      const dec = qs("[data-qty-decrease]", wrap);
      const inc = qs("[data-qty-increase]", wrap);
      if (!input) return;
      dec &&
        dec.addEventListener("click", () => {
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
          input.dispatchEvent(new Event("change"));
        });
      inc &&
        inc.addEventListener("click", () => {
          input.value = (parseInt(input.value, 10) || 1) + 1;
          input.dispatchEvent(new Event("change"));
        });
    });
  }

  /* --------------------------- collection filters --------------------------
     [data-mobile-filters-btn] opens [data-mobile-filter-sheet] (.is-open)
     [data-mobile-filter-sheet-close] closes it
     [data-filter-dropdown-trigger] toggles an `is-open` class on its closest
     [data-filter-dropdown] ancestor (same pattern as initAccordion above),
     which the CSS uses to show/hide the adjacent [data-filter-dropdown-panel]
     and rotate the chevron. Clicking outside an open dropdown, or opening a
     different one, closes the others.
     The sort <select> and price-range inputs submit their own small native
     GET forms directly (see snippets/collection-filters.liquid) — no JS
     needed for those beyond the inline onchange/submit already on the markup.
  --------------------------------------------------------------------------- */
  function initCollectionFilters() {
    const sheet = qs("[data-mobile-filter-sheet]");
    qsa("[data-mobile-filters-btn]").forEach((btn) =>
      btn.addEventListener("click", () => sheet && sheet.classList.add("is-open"))
    );
    qsa("[data-mobile-filter-sheet-close]").forEach((btn) =>
      btn.addEventListener("click", () => sheet && sheet.classList.remove("is-open"))
    );

    const dropdowns = qsa("[data-filter-dropdown]");
    if (!dropdowns.length) return;
    qsa("[data-filter-dropdown-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.stopPropagation();
        const item = trigger.closest("[data-filter-dropdown]");
        if (!item) return;
        const willOpen = !item.classList.contains("is-open");
        dropdowns.forEach((d) => {
          if (d !== item) {
            d.classList.remove("is-open");
            const t = qs("[data-filter-dropdown-trigger]", d);
            if (t) t.setAttribute("aria-expanded", "false");
          }
        });
        item.classList.toggle("is-open", willOpen);
        trigger.setAttribute("aria-expanded", willOpen ? "true" : "false");
      });
    });
    document.addEventListener("click", (e) => {
      dropdowns.forEach((d) => {
        if (!d.contains(e.target)) {
          d.classList.remove("is-open");
          const t = qs("[data-filter-dropdown-trigger]", d);
          if (t) t.setAttribute("aria-expanded", "false");
        }
      });
    });
  }

  /* --------------------------- price range slider ---------------------------
     [data-price-slider][data-floor][data-ceil] wraps two overlaid
     <input type="range" data-price-slider-min|max> thumbs (standard dual-range
     technique: both inputs share the same track, CSS gives only the thumbs
     pointer-events so they can be dragged independently) plus a
     [data-price-slider-range] fill bar between them. Dragging (input event)
     live-updates the fill bar and the Rs. labels in the nearest
     [data-price-slider-min-label]/[data-price-slider-max-label]; releasing
     (change event) submits the enclosing form so the GET request re-filters.
  --------------------------------------------------------------------------- */
  function initPriceSliders() {
    qsa("[data-price-slider]").forEach((slider) => {
      const minInput = qs("[data-price-slider-min]", slider);
      const maxInput = qs("[data-price-slider-max]", slider);
      if (!minInput || !maxInput) return;
      const rangeEl = qs("[data-price-slider-range]", slider);
      const form = slider.closest("form");
      const minLabel = form ? qs("[data-price-slider-min-label]", form) : null;
      const maxLabel = form ? qs("[data-price-slider-max-label]", form) : null;
      const floor = parseFloat(slider.getAttribute("data-floor")) || 0;
      const ceil = parseFloat(slider.getAttribute("data-ceil")) || 100;
      const span = ceil - floor || 1;

      function updateRangeFill() {
        const min = parseFloat(minInput.value);
        const max = parseFloat(maxInput.value);
        const left = ((min - floor) / span) * 100;
        const right = ((max - floor) / span) * 100;
        if (rangeEl) {
          rangeEl.style.left = left + "%";
          rangeEl.style.right = 100 - right + "%";
        }
      }

      function updateLabels() {
        const min = parseFloat(minInput.value);
        const max = parseFloat(maxInput.value);
        if (minLabel) minLabel.textContent = formatMoney(Math.round(min * 100));
        if (maxLabel) maxLabel.textContent = formatMoney(Math.round(max * 100));
      }

      function clamp(movedMin) {
        const min = parseFloat(minInput.value);
        const max = parseFloat(maxInput.value);
        if (min > max) {
          if (movedMin) {
            minInput.value = maxInput.value;
          } else {
            maxInput.value = minInput.value;
          }
        }
      }

      minInput.addEventListener("input", () => {
        clamp(true);
        updateRangeFill();
        updateLabels();
      });
      maxInput.addEventListener("input", () => {
        clamp(false);
        updateRangeFill();
        updateLabels();
      });
      minInput.addEventListener("change", () => form && form.submit());
      maxInput.addEventListener("change", () => form && form.submit());

      updateRangeFill();
      updateLabels();
    });
  }

  /* ------------------------------- product -------------------------------
     Product form: [data-product-form][data-product-id]
     Options: [data-option-button][data-option-index][data-option-value] grouped by data-option-name
     Hidden variant input: [data-variant-id-input]
     Price: [data-product-price] [data-product-compare-price]
     Gallery: [data-product-media][data-media-src] thumbs [data-thumb][data-media-src]
     Buy now: [data-buy-now-btn]
  --------------------------------------------------------------------------- */
  function initProductForm() {
    qsa("[data-product-form]").forEach((form) => {
      let variants;
      try {
        variants = JSON.parse(qs("[data-product-variants-json]", form).textContent);
      } catch (e) {
        variants = null;
      }
      const variantInput = qs("[data-variant-id-input]", form);
      const priceEl = qs("[data-product-price]", form);
      const comparePriceEl = qs("[data-product-compare-price]", form);
      const submitBtn = qs("[data-add-to-cart-submit]", form);
      const buyNowBtn = qs("[data-buy-now-btn]", form);
      const mainMedia = qs("[data-product-media]");

      function currentSelection() {
        const groups = {};
        qsa("[data-option-button].is-active", form).forEach((btn) => {
          groups[btn.getAttribute("data-option-index")] = btn.getAttribute("data-option-value");
        });
        return groups;
      }

      function findVariant() {
        if (!variants) return null;
        const sel = currentSelection();
        return (
          variants.find((v) =>
            [v.option1, v.option2, v.option3].every((val, idx) => {
              const wanted = sel[idx + 1];
              return wanted == null || wanted === val;
            })
          ) || null
        );
      }

      function updateForVariant(variant) {
        if (!variant) {
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = submitBtn.getAttribute("data-sold-out-label") || "Sold Out";
          }
          return;
        }
        if (variantInput) variantInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (comparePriceEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            comparePriceEl.textContent = formatMoney(variant.compare_at_price);
            comparePriceEl.hidden = false;
          } else {
            comparePriceEl.hidden = true;
          }
        }
        if (variant.featured_media && mainMedia) {
          const src = variant.featured_media.preview_image.src;
          setMainMedia(src);
        }
        if (submitBtn) {
          if (variant.available) {
            submitBtn.disabled = false;
            submitBtn.textContent = submitBtn.getAttribute("data-add-label") || "Add to Bag";
          } else {
            submitBtn.disabled = true;
            submitBtn.textContent = submitBtn.getAttribute("data-sold-out-label") || "Sold Out";
          }
        }
        if (buyNowBtn) buyNowBtn.disabled = !variant.available;
      }

      qsa("[data-option-button]", form).forEach((btn) => {
        btn.addEventListener("click", () => {
          const name = btn.getAttribute("data-option-name");
          qsa('[data-option-button][data-option-name="' + name + '"]', form).forEach((b) =>
            b.classList.remove("is-active")
          );
          btn.classList.add("is-active");
          updateForVariant(findVariant());
        });
      });

      if (buyNowBtn) {
        buyNowBtn.addEventListener("click", () => {
          const sizeGroup = qs("[data-size-group]", form);
          if (sizeGroup && !qs("[data-size-swatch].is-active", sizeGroup)) {
            toast("Please select a size.", "error");
            return;
          }
          const id = variantInput ? variantInput.value : null;
          if (!id) return;
          Cart.add(
            (function () {
              const fd = new FormData();
              fd.append("id", id);
              fd.append("quantity", (qs("[data-qty-input]", form) || {}).value || 1);
              return fd;
            })(),
            { silent: true }
          ).then(() => (window.location.href = "/checkout"));
        });
      }

      if (variants) updateForVariant(findVariant());
    });

    function setMainMedia(src) {
      const mainMedia = qs("[data-product-media]");
      if (mainMedia) mainMedia.src = src;
      qsa("[data-thumb]").forEach((t) => t.classList.toggle("is-active", t.getAttribute("data-media-src") === src));
    }

    qsa("[data-thumb]").forEach((thumb) => {
      thumb.addEventListener("click", () => {
        setMainMedia(thumb.getAttribute("data-media-src"));
      });
    });
  }

  /* --------------------------- PDP gallery zoom ----------------------------
     [data-zoom-wrap] wraps [data-product-media] — hover to magnify at the
     cursor position, mirroring the original mousemove zoom effect.
  --------------------------------------------------------------------------- */
  function initGalleryZoom() {
    qsa("[data-zoom-wrap]").forEach((wrap) => {
      const img = qs("[data-product-media]", wrap);
      if (!img) return;
      wrap.addEventListener("mousemove", (e) => {
        const rect = wrap.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + "% " + y + "%";
        img.style.transform = "scale(1.8)";
      });
      wrap.addEventListener("mouseleave", () => {
        img.style.transform = "";
        img.style.transformOrigin = "";
      });
    });
  }

  /* -------------------------------- init --------------------------------- */
  document.addEventListener("DOMContentLoaded", function () {
    initHeader();
    Cart.init();
    Wishlist.init();
    initNewsletter();
    initReveal();
    initHero();
    initAccordion();
    initItemDetailsToggle();
    initQtySteppers();
    initProductForm();
    initCollectionFilters();
    initPriceSliders();
    initGalleryZoom();
  });
})();
